// components/landing/LandingRotator.tsx
'use client';

import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import HeroSection from './HeroSection';
import ControlPanelSection from './ControlPanelSection';
import SystemFlowSection from './SystemFlowSection';

const sections = [
  { id: 'hero', component: HeroSection, name: '메인' },
  { id: 'control', component: ControlPanelSection, name: '제어 패널' },
  { id: 'system', component: SystemFlowSection, name: '시스템 구성' },
];

export default function LandingRotator() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % sections.length);
        setIsTransitioning(false);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  const goToSection = (index: number) => {
    if (index === currentIndex) return;

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 500);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? sections.length - 1 : currentIndex - 1;
    goToSection(newIndex);
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % sections.length;
    goToSection(newIndex);
  };

  const CurrentSection = sections[currentIndex].component;

  return (
    <div className="relative  " onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      {/* 현재 섹션 */}
      <div className={`transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <CurrentSection />
      </div>

      {/* 네비게이션 컨트롤 */}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-full shadow-2xl px-6 py-4 flex items-center gap-6">
          {/* 이전 버튼 */}
          <button
            onClick={goToPrevious}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            aria-label="이전 섹션"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>

          {/* 인디케이터 */}
          <div className="flex items-center gap-2">
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => goToSection(index)}
                className="group relative"
                aria-label={`${section.name} 섹션으로 이동`}
              >
                <div
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-green-600 dark:bg-green-400 w-8'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                />
                {/* 툴팁 */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  {section.name}
                </div>
              </button>
            ))}
          </div>

          {/* 다음 버튼 */}
          <button
            onClick={goToNext}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            aria-label="다음 섹션"
          >
            <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>

          {/* 일시정지 표시 */}
          {isPaused && (
            <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg">
              일시정지됨
            </div>
          )}
        </div>
      </div>

      {/* 프로그레스 바 (선택사항) */}
      {!isPaused && (
        <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-700 z-50">
          <div
            className="h-full bg-green-600 dark:bg-green-400 transition-all duration-5000 ease-linear"
            style={{
              width: isTransitioning ? '0%' : '100%',
            }}
          />
        </div>
      )}
    </div>
  );
}
